import { Injectable } from '@angular/core';
import { PageName, SortBy, sortOptions, ParamsType, PageParams, CONSTANTS, Pager, ScreenInput } from '@app/constants';
import { DefaultIndexDoc } from '@app/graphql';
import { FulltextInput, FacetInput, ConditionInput, SortInput, RangeInput, Url } from '@app/graphql';
import { BehaviorSubject, from, isObservable, Observable, Subscription, of } from 'rxjs';
import { delay, map, switchMap, take, tap, shareReplay } from 'rxjs/operators';
import { createStore, withProps, select, setProp, emitOnce } from '@ngneat/elf';
import { withEntities, selectAllEntities, setEntities, addEntities, selectEntitiesCount, getEntitiesCount, selectActiveEntities, withActiveIds, setActiveIds, resetActiveIds, getEntity, getEntitiesIds } from '@ngneat/elf-entities';
import { withPagination, updatePaginationData, setPage, selectCurrentPageEntities, getPaginationData, hasPage, deleteAllPages } from '@ngneat/elf-pagination';

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  private _pagesStore = new Map<string, any>();

  constructor() {}

  public getStore = (route: any): any => {
    //console.log('StoreService getStore route.pageName ', route.pageName);
    //console.log('StoreService getStore this._pagesStore ', this._pagesStore);
    //console.log('StoreService getStore this._pagesStore.has(route.pageName) ', this._pagesStore.has(route.pageName));

    if (this._pagesStore.has(route.pageName)) {
      return this._pagesStore.get(route.pageName);
    }
    else {
      const _store = this.createStore(route);
      this._pagesStore.set(route.pageName, _store);
      return _store;
    }
  };

  public getExistingStore = (storeName: string): any => this._pagesStore.get(storeName);

  public hasStore = (storeName: string): boolean => this._pagesStore.has(storeName);

  private createStore = (route: any) => {

    const store = createStore(
      { name: route.pageName },
      withEntities<DefaultIndexDoc, 'url'>({idKey: 'url'}),
      withActiveIds(),
      withPagination(),
      withProps({
        status: 'pending',
        count: '',
        storeOffset: 0,
        cacheId: '',
        thumbnails: [] as DefaultIndexDoc[],
        lastScrollPosition: 0,
        pageParams: {
          activeFilters: new Array<string>(),
          queryParams: route.queryParams,
          sortBy: sortOptions[0],
          fullText: route.fullText
        }
      })
    );

    /* VARIABLES */

    const articles$ = store.pipe(selectCurrentPageEntities(), shareReplay({ bufferSize: 1, refCount: true })); 
    const collectionCount$ = store.pipe(selectEntitiesCount(), shareReplay({ bufferSize: 1, refCount: true }));        
    const status$ = store.pipe(
      select((state) => state.status), 
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const count$ = store.pipe(
      select((state) => state.count), 
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const pageParams$ = store.pipe(
      select((state) => state.pageParams), 
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const currentDetail$ = store.pipe(selectActiveEntities(), shareReplay({ bufferSize: 1, refCount: true }));
    const thumbnails$ = store.pipe(
      select((state) => state.thumbnails), 
      shareReplay({ bufferSize: 1, refCount: true })
    );

    /* SETTERS */

    const setStatus = (status: string): void => {
      store.update(setProp('status', status));
    }

    const setCount = (count: string): void => {
      store.update(setProp('count', count));
    }

    const setStoreEntities = (pager: Pager, items: DefaultIndexDoc[]) => {
      //console.log('StoreService setStoreEntities pager ', pager);

      store.update(deleteAllPages());

      const pages = Math.ceil(pager.pageItems.length / pager.limit);
      let currentPage = pager.currentPage === 1 ? pager.currentPage : pager.currentPage - 1;
      let offset = 0;
      //console.log('StoreService setStoreEntities pages ', pages);
      
      
      for (let index = 1; index <= pages; index++) {
        //console.log('StoreService setStoreEntities index ', index);
        //console.log('StoreService setStoreEntities currentPage ', currentPage);
        //console.log('StoreService setStoreEntities offset ', offset);
        
        store.update(
          setPage(
            currentPage,
            pager.pageItems.slice(offset, offset + pager.limit)
          )
        );
        
        currentPage++;
        offset = offset + pager.limit;
      }

      store.update(
        setEntities(items),
        updatePaginationData({
          currentPage: pager.currentPage,
          perPage: pager.limit,
          total: pager.total,
          lastPage: pager.lastPage,
        })
      );
    }

    const addStoreEntities = (pager: Pager, items: DefaultIndexDoc[]) => {

      let currentPage = pager.pageUp ? pager.currentPage + 1 : pager.currentPage - 1;
      //console.log('StoreService addStoreEntities currentPage ', currentPage);
      
      
      store.update(
        setPage(
          currentPage,
          pager.pageItems
        )
      );

      store.update(
        addEntities(items),
        updatePaginationData({
          currentPage: pager.currentPage,
          perPage: pager.limit,
          total: pager.total,
          lastPage: pager.lastPage,
        })
      );
    };  
      

    const setPageParams = (pageParams: PageParams) => {
      store.update((state) => ({
        ...state,
        pageParams
      }));
    }

    const setStoreOffset = (storeOffset: number) => {
      store.update((state) => ({
        ...state,
        storeOffset
      }));
    }

    const setCurrentPage = (pager: Pager): void => {
      store.update(
        updatePaginationData({
          currentPage: pager.currentPage,
          perPage: pager.limit,
          total: pager.total,
          lastPage: pager.lastPage,
        })
      );
    }

    const setCache = (cacheId: string): void => {
      store.update(setProp('cacheId', cacheId));
    }

    const setDetail = (id: string): ScreenInput | boolean => {
      console.log('StoreService setDetail store ', store);
      const ids = store.query(getEntitiesIds());
      const relativeIndex = ids.indexOf(id);

      console.log('StoreService setDetail relativeIndex ', relativeIndex);

      // we don't have the detail in the store, so request next page
      if (relativeIndex < 0) {
        return false;
      }



      const next = relativeIndex < ids.length -1 ? ids[relativeIndex + 1] : false;
      const prev = relativeIndex > 0 ? ids[relativeIndex - 1] : false;
      const detailIndex = relativeIndex + store.state.storeOffset + 1;
      const activeItem = store.query(getEntity(id));
      const thumbnailsIds = ids.slice(relativeIndex + 1, relativeIndex + 5);
      const thumbnails: any[] = [];

      thumbnailsIds.map(id => {
        thumbnails.push(store.query(getEntity(id)));
      });

      store.update(
        setActiveIds([id]),
        setProp('thumbnails', thumbnails)
      );

      return {
        activeItem,
        prev: prev ? store.query(getEntity(prev)) : false,
        next: next ? store.query(getEntity(next)) : false,
        showPlayBtn: activeItem?.content_type === 'article' ? false : true,
        detailIndex
      };
    }

    const resetDetail = (): void => {
      store.update(resetActiveIds());
    }

    const setLastScrollPosition = (lastScrollPosition: number): void => {
      store.update(setProp('lastScrollPosition', lastScrollPosition));
    }

    /* GETTERS */

    const isEmpty = (): boolean => store.query(getEntitiesCount()) === 0;
    const getStoreOffset = (): number => store.getValue().storeOffset;
    const getStoreIds = (): string[] => store.getValue().ids;
    const getCurrentPage = () => store.query(getPaginationData()).currentPage;
    const hasPage = (pageNumber: number): boolean => !!store.getValue().pagination.pages[pageNumber];
    const getTotal = (): string =>  store.getValue().count;
    const getCache = (): string =>  store.getValue().cacheId;
    const getLastScrollPosition = (): number =>  store.getValue().lastScrollPosition;

    return {
      store,
      articles$,
      count$,
      status$,
      pageParams$,
      currentDetail$,
      thumbnails$,

      setStatus,
      setCount,
      setStoreEntities,
      addStoreEntities,
      setPageParams,
      setStoreOffset,
      setCurrentPage,
      setCache,
      setDetail,
      resetDetail,
      setLastScrollPosition,

      isEmpty,
      getStoreOffset,
      getStoreIds,
      getCurrentPage,
      hasPage,
      getTotal,
      getCache,
      getLastScrollPosition
    };
  }
}







